<template>
  <div class="col-md-8 col-lg-4 chart-card-col">
    <div class="chart-card">
      <div class="chart-card-header">
        <h5 class="chart-title">{{ statisticsItem[itemKey].data.name }}</h5>

        <div class="chart-header-controls">
          <select
            v-if="statisticsItem[itemKey].data.is_filterable"
            @change="$emit('onDateChange', statisticsItem[itemKey])"
            v-model="statisticsItem[itemKey].period_type"
            class="chart-range-select"
          >
            <option value="" disabled selected>Select Range</option>
            <option
              v-for="(range, j) in timeRange"
              :key="j"
              :value="range.value"
            >
              {{ range.name }}
            </option>
          </select>

          <div v-if="statisticsItem[itemKey].data.is_warehouse_filterable" class="warehouse-select">
            <v-select
              placeholder="All Warehouses"
              v-model="statisticsItem[itemKey].warehouse_id"
              :options="warehouses"
              label="text"
              :reduce="text => text.id"
              @update:modelValue="(warehouseId) => $emit('onWarehouseChange', statisticsItem[itemKey], warehouseId)"
            />
          </div>
        </div>
      </div>

      <p class="chart-empty-state" v-if="statisticsItem[itemKey].data.labels.length == 0">No data available</p>
      <PieChart v-else
        :chartData="{
          labels: statisticsItem[itemKey].data.labels,
          datasets: statisticsItem[itemKey].data.data_sets
        }"
        :chartOptions="chartOptions"
        :plugins="getPlugins(statisticsItem[itemKey])"
        :styles="chartStyles"
      />
    </div>
  </div>
</template>

<script setup>
import PieChart from '@/components/atom/chart/pieChart';

defineProps({
  statisticsItem: Object,
  page: Object,
  timeRange: Array,
  itemKey: String,
  warehouses: Array
})

const chartStyles = {
  position: 'relative',
  height: '300px'
}

const generateChartDataDetails = (chart) => {
  const data = chart.data;
  const dataset = data.datasets[0];
  const total = dataset.data.reduce((sum, val) => sum + val, 0);

  return data.labels.map((label, i) => {
    const value = dataset.data[i];
    const percentage = ((value / total) * 100).toFixed(1);
    const qty = dataset.qty ? parseFloat(dataset.qty[i].toFixed(4)) : null;

    return {
      index: i,
      label,
      value,
      percentage,
      qty,
      color: dataset.backgroundColor[i],
      borderColor: dataset.backgroundColor[i],
      hidden: chart.getDatasetMeta(0).data[i].hidden
    };
  });
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        boxHeight: 10,
        padding: 14,
        font: { size: 11 },
       generateLabels: (chart) => {
          return generateChartDataDetails(chart)
            .sort((a, b) => b.percentage - a.percentage)
            .map(item => ({
              text: item.qty === null ? `${item.label} - ${item.percentage}%` : `${item.label} - ${item.percentage}% (Qty: ${item.qty})`,
              fillStyle: item.color,
              strokeStyle: item.borderColor,
              lineWidth: 1,
              hidden: item.hidden,
              index: item.index
          }));
        }
      }
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const chart = context.chart;
          const details = generateChartDataDetails(chart)[context.dataIndex];
          if (details.qty === null) return `${details.label}: ${details.value} (${details.percentage}%)`;
          return `${details.label}: ${details.value} (${details.percentage}%) — Qty: ${details.qty}`;
        }
      }
    }
  }
};

const getPlugins = (data) => {
  const punctuality = data.data.punctuality;

  if (punctuality === undefined || punctuality === null) {
    return [];
  }

  return [
    {
      id: 'centerText',
      beforeDraw(chart) {
        const { ctx, width, height } = chart;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#2c3e50';

        ctx.font = 'bold 30px Arial';
        ctx.fillText(`${punctuality}%`, width / 2, height / 2 - 10);

        ctx.font = '12px Arial';
        ctx.fillStyle = '#7f8c8d';
        ctx.fillText('PUNCTUALITY', width / 2, height / 2 + 15);

        ctx.restore();
      }
    }
  ];
};
</script>

<style scoped>
.chart-card {
  background-color: #ffffff;
  border: 1px solid #e8e9ec;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.06);
  padding: 20px;
  height: 100%;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.chart-card-col {
  padding: 8px;
}

.chart-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(34, 41, 47, 0.12);
}

.chart-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f1f4;
}

.chart-title {
  font-size: 14px;
  font-weight: 700;
  color: #2b2b2b;
  margin: 0;
}

.chart-header-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-range-select {
  border: 1px solid #eceef1;
  border-radius: 6px;
  background-color: #f8f9fa;
  font-size: 11px;
  padding: 4px 8px;
  color: #6e6b7b;
}

.chart-empty-state {
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
  padding: 40px 0;
}

.warehouse-select {
  width: 200px;
  font-size: 12px;
}

.warehouse-select :deep(.vs__dropdown-toggle) {
  padding: 0;
}
</style>
